// Encodes a Unicode string and returns a binary array
export function encode(s) {
	if (window.TextEncoder)
		return new TextEncoder().encode(s);

	const encoded = encodeURIComponent(s);
	const binary = encoded.replace(/%([0-9A-F]{2})/g, (match, p1) => String.fromCharCode('0x' + p1));
	const data = new Uint8Array(binary.length);
	Array.prototype.forEach.call(binary, (ch, i) => {
		data[i] = ch.charCodeAt(0);
	});
	return data;
};

//  Decodes a binary array and returns a Unicode string
export function decode(data) {
	if (window.TextDecoder)
		return new TextDecoder().decode(data);

	if (data instanceof ArrayBuffer)
		data = new Uint8Array(data);

	const binary = Array.prototype.map.call(data, ch => String.fromCharCode(ch)).join('');
	const encoded = binary.replace(/(.)/g, (m, p) => {
		let code = p.charCodeAt(0).toString(16).toUpperCase();
		if (code.length < 2)
			code = '0' + code;
		return '%' + code;
	});
	return decodeURIComponent(encoded);
}

export type EncryptedRecord = {
	Data: ArrayBuffer;
	IV: Uint8Array;
}

export async function encrypt(data: any, encryptionKey: CryptoKey, additionalData: ArrayBuffer): Promise<EncryptedRecord> {
	try {
		const iv = crypto.getRandomValues(new Uint8Array(12));
		const alg = { name: 'AES-GCM', iv: iv, additionalData, tagLength: 128 };
		const buffer = await crypto.subtle.encrypt(alg, encryptionKey, encode(JSON.stringify(data)));
		return { Data: buffer, IV: iv };
	}
	catch (e) {
		return null;
	}
}

export function isEncryptedRecord(record: any): record is EncryptedRecord {
	return record.Data && record.IV;
}

// Decrypts the specified encrypted entity
export async function decrypt(encrypted: EncryptedRecord, encryptionKey: CryptoKey, additionalData: ArrayBuffer) {
	try {
		const alg = { name: 'AES-GCM', iv: encrypted.IV, additionalData, tagLength: 128 };
		const buffer = await crypto.subtle.decrypt(alg, encryptionKey, encrypted.Data);
		return JSON.parse(decode(buffer));
	}
	catch (e) {
		return null;
	}
}

// Gets or creates a crypto key for the specified form
export async function isCryptoSupported(keySeed: string) {
	try {
		const crypto = window.crypto || window.msCrypto as Crypto; // for IE11
		if (crypto.webkitSubtle)
			crypto.subtle = window.crypto.webkitSubtle as SubtleCrypto; // for Safari

		const cryptoSeed = await crypto.subtle.digest('SHA-256', encode(keySeed));
		const key = await window.crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
		const test = { Id: '0-0', Validation: '1234$%^&ABCD' };
		const encrypted = await encrypt(test, key, cryptoSeed);
		if (encrypted) {
			const decrypted = await decrypt(encrypted, key, cryptoSeed);
			if (decrypted && decrypted.Validation === test.Validation)
				return cryptoSeed;
		}
	}
	catch (e) { }
}